// client/src/coms/soundboard/audioPrep.js
// Turns any audio the browser can decode (or a microphone recording) into the format the server
// accepts: mono 16-bit PCM WAV at 24 kHz, trimmed, at most MAX_CLIP_SECONDS long.
// Decoding happens in the browser's sandboxed media decoder; the server re-validates everything.

export const TARGET_RATE = 24000;
export const MAX_CLIP_SECONDS = 10;
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

// ---- pure helpers (unit tested) ----

export function mixToMono(channels) {
  const list = (channels || []).filter(Boolean);
  if (!list.length) return new Float32Array(0);
  if (list.length === 1) return Float32Array.from(list[0]);
  const n = Math.min(...list.map((c) => c.length));
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (const c of list) s += c[i];
    out[i] = s / list.length;
  }
  return out;
}

export function trimSamples(samples, rate, startSec, endSec) {
  const len = samples.length;
  const start = Math.max(0, Math.min(len, Math.floor((Number(startSec) || 0) * rate)));
  const endRaw = endSec == null ? len : Math.floor(Number(endSec) * rate);
  const end = Math.max(start, Math.min(len, endRaw));
  return samples.subarray(start, end);
}

// Short fades avoid clicks at cut points.
export function applyFades(samples, rate, fadeMs = 8) {
  const out = Float32Array.from(samples);
  const n = Math.min(Math.floor((rate * fadeMs) / 1000), Math.floor(out.length / 2));
  for (let i = 0; i < n; i++) {
    const g = i / n;
    out[i] *= g;
    out[out.length - 1 - i] *= g;
  }
  return out;
}

export function encodeWav16(samples, rate) {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const w = (o, s) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  w(0, 'RIFF');
  v.setUint32(4, 36 + n * 2, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  w(36, 'data');
  v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? Math.round(s * 32768) : Math.round(s * 32767), true);
  }
  return new Uint8Array(buf);
}

export function peaks(samples, buckets = 120) {
  const out = new Array(buckets).fill(0);
  if (!samples.length) return out;
  const size = Math.max(1, Math.floor(samples.length / buckets));
  for (let b = 0; b < buckets; b++) {
    let m = 0;
    const start = b * size;
    for (let i = start; i < Math.min(samples.length, start + size); i++) m = Math.max(m, Math.abs(samples[i]));
    out[b] = m;
  }
  return out;
}

export async function sha256Hex(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ---- browser decoding ----

let decodeCtx = null;
function getDecodeCtx() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error('This browser cannot decode audio.');
  if (!decodeCtx) decodeCtx = new Ctx();
  return decodeCtx;
}

// -> { samples: Float32Array (mono, TARGET_RATE), rate, duration }
export async function decodeAudioFile(fileOrBlob) {
  if (!fileOrBlob) throw new Error('No audio selected.');
  if (fileOrBlob.size > MAX_SOURCE_BYTES) throw new Error('That file is too large (max 25 MB before trimming).');
  const ab = await fileOrBlob.arrayBuffer();
  let decoded;
  try {
    decoded = await getDecodeCtx().decodeAudioData(ab.slice(0));
  } catch {
    throw new Error('That file is not audio this browser can play.');
  }
  const channels = [];
  for (let c = 0; c < decoded.numberOfChannels; c++) channels.push(decoded.getChannelData(c));
  const mono = mixToMono(channels);
  const samples = await resample(mono, decoded.sampleRate, TARGET_RATE);
  return { samples, rate: TARGET_RATE, duration: samples.length / TARGET_RATE };
}

async function resample(samples, fromRate, toRate) {
  if (fromRate === toRate || !samples.length) return samples;
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const frames = Math.max(1, Math.ceil((samples.length * toRate) / fromRate));
  if (!Offline) {
    const out = new Float32Array(frames);
    for (let i = 0; i < frames; i++) out[i] = samples[Math.min(samples.length - 1, Math.floor((i * fromRate) / toRate))];
    return out;
  }
  const ctx = new Offline(1, frames, toRate);
  const buf = ctx.createBuffer(1, samples.length, fromRate);
  buf.copyToChannel(samples, 0);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(ctx.destination);
  src.start();
  const rendered = await ctx.startRendering();
  return rendered.getChannelData(0).slice();
}

// Clip -> WAV bytes ready for upload.
export function buildClip(samples, rate, startSec, endSec) {
  const cut = trimSamples(samples, rate, startSec, endSec);
  if (cut.length / rate > MAX_CLIP_SECONDS + 0.001) throw new Error(`Clips can be at most ${MAX_CLIP_SECONDS} seconds. Trim it first.`);
  if (cut.length / rate < 0.1) throw new Error('The clip is too short.');
  return encodeWav16(applyFades(cut, rate), rate);
}

// Short microphone recording (MediaRecorder) -> Blob in a browser-decodable format.
export async function recordMicrophone(maxSeconds = MAX_CLIP_SECONDS, { onTick } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  const rec = new MediaRecorder(stream);
  const chunks = [];
  rec.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);
  const done = new Promise((resolve) => {
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      resolve(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
    };
  });
  rec.start(250);
  const started = Date.now();
  const timer = setInterval(() => {
    const s = (Date.now() - started) / 1000;
    if (typeof onTick === 'function') onTick(s);
    if (s >= maxSeconds && rec.state === 'recording') rec.stop();
  }, 200);
  return {
    stop: () => {
      if (rec.state === 'recording') rec.stop();
    },
    result: done.finally(() => clearInterval(timer)),
  };
}
